<script setup lang="ts">
import {
  computed,
  ref,
  unref,
} from 'vue';

import type { DateRangeInputType, DateSingleInputType } from '@modules/forms/types';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VFormDateRangeInput from '@modules/forms/components/inputs/VFormDateRangeInput/VFormDateRangeInput.vue';
import { TRANSLATION_GROUP_DATEPICKER } from '@modules/forms/constants';
import { useI18n } from '@modules/i18n/composables';
import { QA_TABLES } from '@modules/tables/constants';
import { utcDateToLocal } from '@utils/date';
import {
  watchImmediate,
} from '@vueuse/core';

import type {
  ISmartFilterDateEmits,
  ISmartFilterDateProps,
} from './types';

const props = withDefaults(defineProps<ISmartFilterDateProps>(), {
  showClear: true,
});
const emit = defineEmits<ISmartFilterDateEmits>();

const { getTranslation } = useI18n();

const innerValue = ref<DateRangeInputType>();
const startDate = computed<DateSingleInputType>(() => innerValue.value?.[0]);
const endDate = computed<DateSingleInputType>(() => innerValue.value?.[1]);

watchImmediate(() => props.data, (newValue) => {
  innerValue.value = [
    newValue?.start ? utcDateToLocal(newValue?.start) : undefined,
    newValue?.end ? utcDateToLocal(newValue?.end) : undefined,
  ];
}, { deep: true });

function translate(shortCode: string): string {
  return unref(getTranslation(TRANSLATION_GROUP_DATEPICKER, shortCode));
}

function onChange(value?: DateRangeInputType): void {
  innerValue.value = value;

  emit('change', {
    start: startDate.value ?? null,
    end: endDate.value ?? null,
  });
}

function clear(): void {
  onChange();
}
</script>

<template>
  <div v-data-qa-auto-id="'VSmartFilterDateRange'"
    v-data-test="{ el: QA_TABLES.filterDateRangeRoot }"
    class="flex items-center gap-4"
  >
    <div class="grow">
      <VFormDateRangeInput
        name="dateRange"
        is-full-width
        :value="innerValue"
        :label="translate('RANGE_START')"
        :label-end-date="translate('RANGE_END')"
        :max-range="{ value: 1, unit: 'year' }"
        :disabled="isSupervision"
        hide-errors
        @change="onChange"
      />
    </div>
    <div
      v-if="showClear"
    >
      <VIconButton
        v-data-test="{ el: QA_TABLES.filterDateRangeClear }"
        icon="pi pi-times"
        class="mt-1 flex-none"
        :disabled="(!startDate && !endDate) || isSupervision"
        @click="clear"
      />
    </div>
  </div>
</template>
